import { useQuery, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { useWorkspaceKeys, type WorkspaceKeys } from "../../app/current-user.tsx";
import { researchApi } from "../../client.ts";
import type {
  CreateLandInput,
  Land,
  LandDeletion,
  LandDetail,
  LandInput,
  LandSummary,
  UserSummary,
} from "../../domain/schema.ts";
import { useWorkspaceMutation } from "../../lib/workspace-mutation.ts";
import { workspaceQuery, type WorkspaceQuery } from "../../lib/workspace-query.ts";

export const landsQuery = (keys: WorkspaceKeys): WorkspaceQuery<LandSummary[]> =>
  workspaceQuery(keys.lands(), () => researchApi.listLands());

export const landQuery = (keys: WorkspaceKeys, id: string): WorkspaceQuery<LandDetail> =>
  workspaceQuery(keys.land(id), () => researchApi.getLand(id));

export const useLands = (): UseQueryResult<LandSummary[]> =>
  useQuery(landsQuery(useWorkspaceKeys()));

export const useLand = (id: string): UseQueryResult<LandDetail> =>
  useQuery(landQuery(useWorkspaceKeys(), id));

// Land writes answer a `Land`, not the `LandDetail` its page shows, so the detail is
// refreshed instead of written.
export const useCreateLand = (): UseMutationResult<Land, Error, CreateLandInput> =>
  useWorkspaceMutation({ mutationFn: (input: CreateLandInput) => researchApi.createLand(input) });

/** Analysts only: the accounts a new land can belong to. */
export const useAdminUsers = (): UseQueryResult<UserSummary[]> => {
  const keys = useWorkspaceKeys();
  return useQuery({
    queryKey: [...keys.all, "admin-users"],
    queryFn: () => researchApi.admin.listUsers(),
  });
};

export const useUpdateLand = (id: string): UseMutationResult<Land, Error, LandInput> =>
  useWorkspaceMutation({ mutationFn: (input: LandInput) => researchApi.updateLand(id, input) });

/** Deleted or archived, the land is gone for the caller; its page navigates away. */
export const useDeleteLand = (): UseMutationResult<LandDeletion, Error, string> =>
  useWorkspaceMutation({
    mutationFn: (id: string) => researchApi.deleteLand(id),
    cache: (keys, _deletion, id) => ({ remove: [keys.land(id)] }),
  });

/** Analysts only: the owner sees the archived land again. */
export const useRestoreLand = (): UseMutationResult<Land, Error, string> =>
  useWorkspaceMutation({ mutationFn: (id: string) => researchApi.admin.restoreLand(id) });
